import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';

const MasterAnalytics = () => {
    // --- AUTH STATE ---
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authLoading, setAuthLoading] = useState(false);
    const [authError, setAuthError] = useState(null);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    // --- VIEW STATE ---
    const [activeTab, setActiveTab] = useState('analytics'); // 'analytics' | 'library'

    // --- DATA STATE (Analytics) ---
    const [restaurants, setRestaurants] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const navigate = useNavigate();

    // --- 3D LIBRARY STATE ---
    const [libraryModels, setLibraryModels] = useState([]);
    const [libraryLoading, setLibraryLoading] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadForm, setUploadForm] = useState({ name: '', tags: '' });
    const [uploadModelFile, setUploadModelFile] = useState(null);
    const [uploadThumbnailFile, setUploadThumbnailFile] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [librarySearch, setLibrarySearch] = useState('');
    const [editingModel, setEditingModel] = useState(null);

    // --- ADMIN EMAIL WHITELIST ---
    const ADMIN_EMAILS = [
        'pator.explore.tech@gmail.com',
    ];

    // --- LOGIN HANDLER ---
    const handleLogin = async (e) => {
        e.preventDefault();
        setAuthLoading(true);
        setAuthError(null);
        try {
            const { data, error } = await supabase.auth.signInWithPassword({
                email: email.trim(),
                password: password,
            });
            if (error) throw error;
            if (!ADMIN_EMAILS.includes(data.user.email.toLowerCase())) {
                await supabase.auth.signOut();
                setAuthError('Access denied. You are not authorized to view this page.');
                setAuthLoading(false);
                return;
            }
            setIsAuthenticated(true);
        } catch (err) {
            setAuthError(err.message || 'Login failed. Please check your credentials.');
        } finally {
            setAuthLoading(false);
        }
    };

    const handleLogout = async () => {
        await supabase.auth.signOut();
        setIsAuthenticated(false);
        setEmail('');
        setPassword('');
    };

    // --- FETCH DATA ---
    useEffect(() => {
        if (isAuthenticated) {
            fetchData();
            fetchLibraryModels();
        }
    }, [isAuthenticated]);

    const fetchData = async () => {
        try {
            const { data: restoData, error } = await supabase
                .from('restaurants').select('*').order('created_at', { ascending: false });
            if (error) throw error;
            setRestaurants(restoData || []);

            const { data: subData } = await supabase
                .from('subscriptions').select('*, plans(name, slug, price)')
                .order('created_at', { ascending: false });
            setSubscriptions(subData || []);
        } catch (err) {
            console.error("Error fetching analytics:", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchLibraryModels = async () => {
        setLibraryLoading(true);
        try {
            const { data, error } = await supabase
                .from('model_library').select('*').order('created_at', { ascending: false });
            if (error) throw error;
            setLibraryModels(data || []);
        } catch (err) {
            console.error("Error fetching library:", err);
        } finally {
            setLibraryLoading(false);
        }
    };

    // --- Helper: extract storage path from public URL ---
    const getStoragePath = (publicUrl) => {
        if (!publicUrl) return null;
        const cleanUrl = publicUrl.split('?')[0]; // Remove query params
        const match = cleanUrl.match(/\/storage\/v1\/object\/public\/models\/(.+)/);
        return match ? match[1] : null;
    };

    // --- Helper: upload file with progress tracking ---
    const uploadWithProgress = (bucket, filePath, file) => {
        return new Promise((resolve, reject) => {
            const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
            const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
            const url = `${supabaseUrl}/storage/v1/object/${bucket}/${filePath}`;

            const xhr = new XMLHttpRequest();
            xhr.open('POST', url, true);
            xhr.setRequestHeader('Authorization', `Bearer ${supabaseKey}`);
            xhr.setRequestHeader('apikey', supabaseKey);
            xhr.setRequestHeader('x-upsert', 'true');

            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                    setUploadProgress(Math.round((e.loaded / e.total) * 100));
                }
            };

            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve();
                } else {
                    reject(new Error(`Upload failed with status ${xhr.status}`));
                }
            };

            xhr.onerror = () => reject(new Error('Upload failed: network error'));
            xhr.send(file);
        });
    };

    // --- 3D LIBRARY: UPLOAD ---
    const handleUploadModel = async (e) => {
        e.preventDefault();
        if (!uploadModelFile) { alert('Please select a .glb model file.'); return; }
        setUploading(true);
        setUploadProgress(0);

        try {
            // 1. Upload .glb model (with progress)
            const modelExt = uploadModelFile.name.split('.').pop();
            const modelFileName = `library/${Date.now()}-${uploadForm.name.replace(/\s+/g, '_')}.${modelExt}`;
            await uploadWithProgress('models', modelFileName, uploadModelFile);
            const { data: modelUrlData } = supabase.storage.from('models').getPublicUrl(modelFileName);

            // 2. Upload thumbnail (if provided) — small file, no progress needed
            setUploadProgress(100);
            let thumbnailUrl = '';
            if (uploadThumbnailFile) {
                const thumbExt = uploadThumbnailFile.name.split('.').pop();
                const thumbFileName = `library/thumbs/${Date.now()}-thumb.${thumbExt}`;
                const { error: thumbErr } = await supabase.storage.from('models').upload(thumbFileName, uploadThumbnailFile, { upsert: true });
                if (thumbErr) throw thumbErr;
                const { data: thumbUrlData } = supabase.storage.from('models').getPublicUrl(thumbFileName);
                thumbnailUrl = thumbUrlData.publicUrl;
            }

            // 3. Parse tags: split by comma, trim, lowercase
            const tags = uploadForm.tags
                .split(',')
                .map(t => t.trim().toLowerCase())
                .filter(Boolean);

            // Auto-add the name words as tags
            const nameWords = uploadForm.name.toLowerCase().split(/\s+/).filter(w => w.length > 2);
            const allTags = [...new Set([...tags, ...nameWords])];

            // 4. Insert into model_library
            const modelData = {
                name: uploadForm.name,
                tags: allTags,
                thumbnail_url: thumbnailUrl,
                model_url: modelUrlData.publicUrl,
                file_size_kb: Math.round(uploadModelFile.size / 1024),
            };

            if (editingModel) {
                const { error } = await supabase.from('model_library')
                    .update(modelData).eq('id', editingModel.id);
                if (error) throw error;
            } else {
                const { error } = await supabase.from('model_library').insert([modelData]);
                if (error) throw error;
            }

            // 5. Reset & refresh
            setShowUploadModal(false);
            setUploadForm({ name: '', tags: '' });
            setUploadModelFile(null);
            setUploadThumbnailFile(null);
            setThumbnailPreview('');
            setEditingModel(null);
            fetchLibraryModels();
        } catch (err) {
            alert('Upload failed: ' + err.message);
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const handleDeleteModel = async (model) => {
        if (!window.confirm(`Delete "${model.name}" from the library?`)) return;
        try {
            // 1. Delete files from Supabase Storage
            const modelPath = getStoragePath(model.model_url);
            const thumbPath = getStoragePath(model.thumbnail_url);

            const pathsToDelete = [modelPath, thumbPath].filter(Boolean);
            if (pathsToDelete.length > 0) {
                await supabase.storage.from('models').remove(pathsToDelete);
            }

            // 2. Delete from database
            const { error } = await supabase.from('model_library').delete().eq('id', model.id);
            if (error) throw error;
            setLibraryModels(prev => prev.filter(m => m.id !== model.id));
        } catch (err) {
            alert('Delete failed: ' + err.message);
        }
    };

    const openEditModal = (model) => {
        setEditingModel(model);
        setUploadForm({
            name: model.name,
            tags: (model.tags || []).join(', '),
        });
        setThumbnailPreview(model.thumbnail_url || '');
        setUploadModelFile(null);
        setUploadThumbnailFile(null);
        setShowUploadModal(true);
    };

    // --- LIBRARY FILTERING ---
    const filteredLibrary = libraryModels.filter(m => {
        const matchesSearch = librarySearch === '' ||
            m.name.toLowerCase().includes(librarySearch.toLowerCase()) ||
            (m.tags || []).some(t => t.includes(librarySearch.toLowerCase()));
        return matchesSearch;
    });

    // --- ANALYTICS HELPERS ---
    const getPlanName = (r) => {
        if (r.is_ultimate) return 'Ultimate 💎';
        if (r.is_pro) return 'Pro 🔥';
        return 'Free 🧊';
    };

    const getPlanColor = (r) => {
        if (r.is_ultimate) return 'bg-purple-100 text-purple-800 border-purple-200';
        if (r.is_pro) return 'bg-orange-100 text-orange-800 border-orange-200';
        return 'bg-gray-100 text-gray-800 border-gray-200';
    };

    const getSubForRestaurant = (restoId) => {
        return subscriptions.find(s => s.restaurant_id === restoId && s.status === 'active');
    };

    const filteredRestaurants = restaurants.filter(r => {
        if (filter === 'all') return true;
        if (filter === 'ultimate') return r.is_ultimate;
        if (filter === 'pro') return r.is_pro && !r.is_ultimate;
        if (filter === 'free') return !r.is_pro;
        return true;
    });

    const counts = {
        all: restaurants.length,
        ultimate: restaurants.filter(r => r.is_ultimate).length,
        pro: restaurants.filter(r => r.is_pro && !r.is_ultimate).length,
        free: restaurants.filter(r => !r.is_pro).length
    };

    const activeSubscriptions = subscriptions.filter(s => s.status === 'active');
    const totalMRR = activeSubscriptions.reduce((acc, s) => acc + (s.amount_paid || 0), 0);

    // ========================
    // LOGIN SCREEN
    // ========================
    if (!isAuthenticated) {
        return (
            <div
                className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #0f0f11 0%, #1a1025 40%, #0f0f11 100%)' }}
            >
                <div className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] rounded-full opacity-20 blur-3xl"
                    style={{ background: 'radial-gradient(circle, #7c3aed 0%, transparent 70%)' }}
                />
                <div className="absolute bottom-[-15%] right-[-10%] w-[400px] h-[400px] rounded-full opacity-15 blur-3xl"
                    style={{ background: 'radial-gradient(circle, #ee5b2b 0%, transparent 70%)' }}
                />
                <div className="absolute inset-0 opacity-[0.03]"
                    style={{
                        backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
                        backgroundSize: '60px 60px'
                    }}
                />

                <div className="relative z-10 w-full max-w-sm">
                    <div className="backdrop-blur-xl bg-white/[0.07] border border-white/10 p-10 rounded-[2rem] shadow-2xl text-center relative overflow-hidden">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[200px] h-[1px] bg-gradient-to-r from-transparent via-purple-400/50 to-transparent" />
                        <div className="mb-8">
                            <div className="w-20 h-20 mx-auto mb-5 bg-gradient-to-br from-purple-500/20 to-indigo-500/20 rounded-2xl flex items-center justify-center border border-purple-500/20 shadow-lg backdrop-blur-sm">
                                <span className="text-4xl">📊</span>
                            </div>
                            <h2 className="text-2xl font-black text-white mb-2 tracking-tight">
                                Master <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-300">Analytics</span>
                            </h2>
                            <p className="text-gray-400 text-sm font-medium">Admin access only. Sign in to continue.</p>
                        </div>

                        {authError && (
                            <div className="bg-red-500/10 text-red-400 p-3 rounded-xl text-xs mb-6 text-left border border-red-500/20 backdrop-blur-sm font-medium">
                                ⚠️ {authError}
                            </div>
                        )}

                        <form onSubmit={handleLogin} className="space-y-4 text-left">
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Email</label>
                                <input type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)}
                                    placeholder="admin@example.com"
                                    className="w-full py-3.5 px-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 font-medium outline-none focus:bg-white/10 focus:border-purple-500/50 transition-all text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Password</label>
                                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="w-full py-3.5 px-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 font-medium outline-none focus:bg-white/10 focus:border-purple-500/50 transition-all text-sm"
                                />
                            </div>
                            <button type="submit" disabled={authLoading}
                                className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold rounded-xl shadow-[0_0_30px_rgba(124,58,237,0.3)] hover:shadow-[0_0_40px_rgba(124,58,237,0.4)] transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                            >
                                {authLoading ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Authenticating...
                                    </span>
                                ) : '🔐 Sign In'}
                            </button>
                        </form>

                        <div className="flex items-center gap-3 my-6">
                            <div className="flex-1 h-px bg-white/10" />
                            <span className="text-gray-500 text-xs font-medium uppercase tracking-widest">Restricted Access</span>
                            <div className="flex-1 h-px bg-white/10" />
                        </div>
                        <button onClick={() => navigate('/')} className="text-gray-500 text-xs font-medium hover:text-white transition-colors">
                            ← Back to TableAR
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) return <div className="min-h-screen flex items-center justify-center font-bold text-gray-500">Loading Master Analytics...</div>;

    // ========================
    // MAIN DASHBOARD
    // ========================
    return (
        <div className="min-h-screen bg-gray-50 font-sans text-gray-900 p-8">
            <div className="max-w-7xl mx-auto">

                {/* Header */}
                <header className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-3xl font-black text-gray-900 tracking-tight">Master Analytics 📊</h1>
                        <p className="text-gray-500 mt-1 text-sm">Admin dashboard for TableAR</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-xs font-bold text-gray-400 bg-gray-100 px-3 py-1.5 rounded-lg border border-gray-200">
                            🔓 {email}
                        </span>
                        <button onClick={handleLogout} className="px-4 py-2 bg-white border border-gray-200 rounded-xl font-bold hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors text-sm">
                            Sign Out
                        </button>
                        <button onClick={() => navigate('/')} className="px-4 py-2 bg-white border border-gray-200 rounded-xl font-bold hover:bg-gray-50 transition-colors text-sm">
                            Exit
                        </button>
                    </div>
                </header>

                {/* TAB NAVIGATION */}
                <div className="flex gap-2 mb-8 bg-white rounded-2xl p-1.5 border border-gray-200 shadow-sm w-fit">
                    <button
                        onClick={() => setActiveTab('analytics')}
                        className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'analytics'
                            ? 'bg-gray-900 text-white shadow-md'
                            : 'text-gray-500 hover:bg-gray-50'
                            }`}
                    >
                        📊 Analytics
                    </button>
                    <button
                        onClick={() => setActiveTab('library')}
                        className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center gap-2 ${activeTab === 'library'
                            ? 'bg-gray-900 text-white shadow-md'
                            : 'text-gray-500 hover:bg-gray-50'
                            }`}
                    >
                        📦 3D Model Library
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${activeTab === 'library' ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                            {libraryModels.length}
                        </span>
                    </button>
                </div>

                {/* ======================== */}
                {/* TAB: ANALYTICS          */}
                {/* ======================== */}
                {activeTab === 'analytics' && (
                    <>
                        {/* Stats Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-10">
                            <button onClick={() => setFilter('all')}
                                className={`text-left p-6 rounded-2xl shadow-sm border transition-all ${filter === 'all' ? 'bg-gray-900 text-white ring-4 ring-gray-200 border-transparent transform scale-105' : 'bg-white text-gray-900 border-gray-100 hover:border-gray-300'}`}
                            >
                                <h3 className="text-xs font-bold uppercase tracking-wider mb-2 text-gray-400">Total Users</h3>
                                <p className="text-4xl font-black">{counts.all}</p>
                            </button>
                            <button onClick={() => setFilter('ultimate')}
                                className={`text-left p-6 rounded-2xl shadow-sm border transition-all ${filter === 'ultimate' ? 'bg-purple-600 text-white ring-4 ring-purple-100 border-transparent transform scale-105' : 'bg-white text-gray-900 border-gray-100 hover:border-purple-200'}`}
                            >
                                <h3 className={`text-xs font-bold uppercase tracking-wider mb-2 ${filter === 'ultimate' ? 'text-purple-200' : 'text-gray-400'}`}>Ultimate Plan</h3>
                                <p className={`text-4xl font-black ${filter === 'ultimate' ? 'text-white' : 'text-purple-600'}`}>{counts.ultimate}</p>
                            </button>
                            <button onClick={() => setFilter('pro')}
                                className={`text-left p-6 rounded-2xl shadow-sm border transition-all ${filter === 'pro' ? 'bg-orange-500 text-white ring-4 ring-orange-100 border-transparent transform scale-105' : 'bg-white text-gray-900 border-gray-100 hover:border-orange-200'}`}
                            >
                                <h3 className={`text-xs font-bold uppercase tracking-wider mb-2 ${filter === 'pro' ? 'text-orange-100' : 'text-gray-400'}`}>Pro Plan</h3>
                                <p className={`text-4xl font-black ${filter === 'pro' ? 'text-white' : 'text-orange-500'}`}>{counts.pro}</p>
                            </button>
                            <button onClick={() => setFilter('free')}
                                className={`text-left p-6 rounded-2xl shadow-sm border transition-all ${filter === 'free' ? 'bg-gray-500 text-white ring-4 ring-gray-200 border-transparent transform scale-105' : 'bg-white text-gray-900 border-gray-100 hover:border-gray-300'}`}
                            >
                                <h3 className={`text-xs font-bold uppercase tracking-wider mb-2 ${filter === 'free' ? 'text-gray-200' : 'text-gray-400'}`}>Free Plan</h3>
                                <p className={`text-4xl font-black ${filter === 'free' ? 'text-white' : 'text-gray-400'}`}>{counts.free}</p>
                            </button>
                            <div className="p-6 rounded-2xl shadow-sm border bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                                <h3 className="text-xs font-bold uppercase tracking-wider mb-2 text-green-600">Monthly Revenue</h3>
                                <p className="text-4xl font-black text-green-700">₹{totalMRR.toLocaleString()}</p>
                                <p className="text-xs text-green-500 mt-1 font-medium">{activeSubscriptions.length} active subscription{activeSubscriptions.length !== 1 ? 's' : ''}</p>
                            </div>
                        </div>

                        {/* Data Table */}
                        <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100 animate-fade-in">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50 border-b border-gray-100 text-xs uppercase font-bold text-gray-500 tracking-wider">
                                            <th className="p-5">Restaurant Name</th>
                                            <th className="p-5">Current Plan</th>
                                            <th className="p-5">Amount Paid</th>
                                            <th className="p-5">Subscribed On</th>
                                            <th className="p-5">Expires On</th>
                                            <th className="p-5">Status</th>
                                            <th className="p-5 text-right">Joined</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {filteredRestaurants.map((r) => {
                                            const sub = getSubForRestaurant(r.id);
                                            return (
                                                <tr key={r.id} className="hover:bg-gray-50/50 transition-colors group">
                                                    <td className="p-5 font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                                                        {r.name || "Unnamed Restaurant"}
                                                    </td>
                                                    <td className="p-5">
                                                        <span className={`px-3 py-1 rounded-full text-xs font-extrabold border ${getPlanColor(r)}`}>
                                                            {getPlanName(r)}
                                                        </span>
                                                    </td>
                                                    <td className="p-5 font-bold text-gray-900">
                                                        {sub ? `₹${sub.amount_paid}` : <span className="text-gray-300">—</span>}
                                                    </td>
                                                    <td className="p-5 text-sm text-gray-600 font-mono">
                                                        {sub?.start_date ? new Date(sub.start_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : <span className="text-gray-300">—</span>}
                                                    </td>
                                                    <td className="p-5 text-sm font-mono">
                                                        {sub?.end_date ? (
                                                            <span className={new Date(sub.end_date) < new Date() ? 'text-red-500 font-bold' : 'text-gray-600'}>
                                                                {new Date(sub.end_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                            </span>
                                                        ) : <span className="text-gray-300">—</span>}
                                                    </td>
                                                    <td className="p-5">
                                                        {sub ? (
                                                            <span className={`px-2 py-1 rounded-lg text-xs font-bold ${sub.status === 'active' ? 'bg-green-100 text-green-700' :
                                                                sub.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                                                    sub.status === 'expired' ? 'bg-yellow-100 text-yellow-700' :
                                                                        'bg-gray-100 text-gray-600'
                                                                }`}>
                                                                {sub.status.toUpperCase()}
                                                            </span>
                                                        ) : <span className="text-gray-300 text-xs">N/A</span>}
                                                    </td>
                                                    <td className="p-5 text-right text-sm text-gray-500 font-medium font-mono">
                                                        {new Date(r.created_at).toLocaleDateString()}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            {filteredRestaurants.length === 0 && (
                                <div className="p-12 text-center text-gray-400 font-medium">No results found for this filter.</div>
                            )}
                        </div>
                    </>
                )}

                {/* ======================== */}
                {/* TAB: 3D MODEL LIBRARY   */}
                {/* ======================== */}
                {activeTab === 'library' && (
                    <>
                        {/* Library Header */}
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900">3D Model Library</h2>
                                <p className="text-gray-500 text-sm mt-1">Upload and manage 3D models that restaurant owners can use in their menus.</p>
                            </div>
                            <button
                                onClick={() => { setEditingModel(null); setUploadForm({ name: '', tags: '' }); setThumbnailPreview(''); setUploadModelFile(null); setUploadThumbnailFile(null); setShowUploadModal(true); }}
                                className="bg-gray-900 text-white px-6 py-3 rounded-xl font-bold hover:bg-black shadow-lg flex items-center gap-2 transition-all active:scale-95"
                            >
                                <span className="text-lg">+</span> Upload 3D Model
                            </button>
                        </div>

                        {/* Search */}
                        <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-6 shadow-sm">
                            <div className="relative">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                <input
                                    type="text"
                                    placeholder="Search by name or tag..."
                                    value={librarySearch}
                                    onChange={(e) => setLibrarySearch(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl outline-none focus:border-gray-400 transition-colors text-sm font-medium"
                                />
                            </div>
                        </div>

                        {/* Model Grid */}
                        {libraryLoading ? (
                            <div className="py-20 text-center text-gray-400 font-bold">Loading library...</div>
                        ) : filteredLibrary.length === 0 ? (
                            <div className="py-20 text-center">
                                <div className="text-5xl mb-4">📦</div>
                                <p className="text-gray-400 font-bold">
                                    {librarySearch ? 'No models match your search.' : 'No models uploaded yet.'}
                                </p>
                                <p className="text-gray-400 text-sm mt-1">Click "Upload 3D Model" to add your first model.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                {filteredLibrary.map(model => (
                                    <div key={model.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-lg hover:border-gray-200 transition-all group">
                                        {/* Thumbnail */}
                                        <div className="aspect-square bg-gray-100 relative overflow-hidden">
                                            {model.thumbnail_url ? (
                                                <img src={model.thumbnail_url} alt={model.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-5xl text-gray-300">📦</div>
                                            )}

                                            {/* File size */}
                                            {model.file_size_kb && (
                                                <span className="absolute top-3 right-3 bg-white/80 backdrop-blur-sm text-gray-700 text-[10px] font-bold px-2 py-1 rounded-full">
                                                    {model.file_size_kb > 1024 ? `${(model.file_size_kb / 1024).toFixed(1)}MB` : `${model.file_size_kb}KB`}
                                                </span>
                                            )}
                                        </div>

                                        {/* Info */}
                                        <div className="p-4">
                                            <h3 className="font-bold text-gray-900 truncate mb-1">{model.name}</h3>
                                            {/* Tags */}
                                            <div className="flex flex-wrap gap-1 mb-3">
                                                {(model.tags || []).slice(0, 4).map((tag, i) => (
                                                    <span key={i} className="bg-gray-100 text-gray-500 text-[10px] font-bold px-2 py-0.5 rounded-full">{tag}</span>
                                                ))}
                                                {(model.tags || []).length > 4 && (
                                                    <span className="text-gray-400 text-[10px] font-bold">+{model.tags.length - 4}</span>
                                                )}
                                            </div>
                                            {/* Actions */}
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => openEditModal(model)}
                                                    className="flex-1 py-2 text-sm font-bold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteModel(model)}
                                                    className="py-2 px-3 text-sm font-bold text-red-500 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ======================== */}
            {/* UPLOAD MODAL             */}
            {/* ======================== */}
            {showUploadModal && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 animate-fade-in" onClick={() => setShowUploadModal(false)}>
                    <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className="p-6 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-gray-900">{editingModel ? 'Edit 3D Model' : 'Upload 3D Model'}</h3>
                            <button onClick={() => setShowUploadModal(false)} className="text-gray-400 hover:text-black font-bold text-xl">✕</button>
                        </div>

                        {/* Modal Form */}
                        <form onSubmit={handleUploadModel} className="p-6 space-y-5">
                            {/* Name */}
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Model Name *</label>
                                <input
                                    type="text" required autoFocus
                                    placeholder="e.g. Butter Chicken Bowl"
                                    value={uploadForm.name}
                                    onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                                    className="w-full p-3 border border-gray-200 rounded-xl font-bold outline-none focus:border-gray-400 transition-colors"
                                />
                            </div>



                            {/* Tags */}
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Tags (comma-separated)</label>
                                <input
                                    type="text"
                                    placeholder="e.g. curry, chicken, gravy, bowl, indian"
                                    value={uploadForm.tags}
                                    onChange={(e) => setUploadForm({ ...uploadForm, tags: e.target.value })}
                                    className="w-full p-3 border border-gray-200 rounded-xl outline-none focus:border-gray-400 transition-colors text-sm font-medium"
                                />
                                <p className="text-[11px] text-gray-400 mt-1">Words from the model name are auto-added as tags. Add extra keywords for better searchability.</p>
                            </div>

                            {/* File Uploads */}
                            <div className="grid grid-cols-2 gap-4">
                                {/* GLB File */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">3D Model (.glb) {!editingModel && '*'}</label>
                                    <div className="relative h-28 bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer">
                                        <span className="text-2xl mb-1">📦</span>
                                        <span className="text-gray-400 text-xs font-medium text-center px-2">
                                            {uploadModelFile ? uploadModelFile.name : (editingModel ? 'Upload new (optional)' : 'Click to select .glb')}
                                        </span>
                                        {uploadModelFile && (
                                            <span className="text-[10px] text-gray-400 mt-1">{(uploadModelFile.size / 1024 / 1024).toFixed(1)}MB</span>
                                        )}
                                        <input
                                            type="file" accept=".glb"
                                            required={!editingModel}
                                            className="absolute inset-0 opacity-0 cursor-pointer"
                                            onChange={(e) => setUploadModelFile(e.target.files[0])}
                                        />
                                    </div>
                                </div>

                                {/* Thumbnail */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Preview Image</label>
                                    <div className="relative h-28 bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center overflow-hidden hover:bg-gray-100 transition-colors cursor-pointer">
                                        {thumbnailPreview ? (
                                            <img src={thumbnailPreview} className="w-full h-full object-cover" alt="Thumb" />
                                        ) : (
                                            <div className="flex flex-col items-center">
                                                <span className="text-2xl mb-1">🖼️</span>
                                                <span className="text-gray-400 text-xs">Click to upload</span>
                                            </div>
                                        )}
                                        <input
                                            type="file" accept="image/*"
                                            className="absolute inset-0 opacity-0 cursor-pointer"
                                            onChange={(e) => {
                                                const f = e.target.files[0];
                                                if (f) { setUploadThumbnailFile(f); setThumbnailPreview(URL.createObjectURL(f)); }
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={() => setShowUploadModal(false)} className="flex-1 py-3 font-bold text-gray-500 hover:bg-gray-100 rounded-xl transition-colors">Cancel</button>
                                <button type="submit" disabled={uploading} className="flex-1 py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-black shadow-lg disabled:opacity-50 transition-all relative overflow-hidden">
                                    {uploading && uploadProgress < 100 && (
                                        <div className="absolute inset-0 bg-green-500/20" style={{ width: `${uploadProgress}%`, transition: 'width 0.3s ease' }} />
                                    )}
                                    <span className="relative z-10">
                                        {uploading ? (
                                            <span className="flex items-center justify-center gap-2">
                                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                {uploadProgress < 100 ? `Uploading ${uploadProgress}%` : 'Saving...'}
                                            </span>
                                        ) : editingModel ? 'Save Changes' : 'Upload Model'}
                                    </span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MasterAnalytics;
